/* eslint-disable max-lines-per-function */
/* istanbul ignore file */
import React, { useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { useLazyQuery } from '@apollo/client';
import { withCookies } from '@catalogo/core-connect/cookies';
import { getCoordinatesMobileAcom } from '@catalogo/service-americanas-product/src/queries/coordinates.graphql';
import { addMonths } from '@catalogo/core-cookies/src/helpers';
import { formatMask } from './helpers/mask';
import FreightErrors from './components/freight-errors';
import Address from './components/address';

// eslint-disable-next-line max-statements
export const CepField = ({
  cookies,
  setCookie,
  error: freightError,
  label,
  address = true,
  onClick,
  showMessageError = true,
  callbackError,
}) => {
  const errorValue = showMessageError ? freightError : null;

  const { persistentCep } = cookies;
  const [cep, setCep] = useState(persistentCep);
  const [error, setError] = useState(errorValue);
  const [focus, setFocus] = useState(false);

  const inputRef = useRef(null);

  const [fetchCoordinates] = useLazyQuery(getCoordinatesMobileAcom);

  const updateLocationFeather = () => {
    try {
      feather.modules.location.update();
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    let errorData = freightError?.errorCode !== 'INCOMPLETE_CEP' ? freightError : null;
    debugger;
    if (cep?.length === 8) {
      setCookie('persistentCep', cep, addMonths(new Date(), 12));

      fetchCoordinates({
        variables: { cep },
      });

      if (window.feather) {
        updateLocationFeather();
      }
    } else {
      errorData = { errorCode: 'INCOMPLETE_CEP' };
    }

    if (showMessageError) {
      setError(errorData);
    }

    !!callbackError && callbackError(errorData);

    handleBlur();
  };

  const handleChange = e => {
    const value = (e?.target?.value ?? '').replace(/\D/g, '');
    setCep(value);
  };

  const handleClick = () => {
    !!onClick && onClick();
    handleSubmit;
  };

  const handleFocus = () => {
    setFocus(true);
    inputRef?.current?.focus();
  };

  const handleBlur = () => {
    setFocus(false);
    inputRef?.current?.blur();
  };

  useEffect(() => {
    setError(errorValue);
  }, [freightError]);

  if (address && persistentCep && !error) {
    return <Address />;
  }

  const isInvalidCep = error?.errorCode === 'INVALID_CEP' && !focus && cep === persistentCep;
  const isIncompleteCep = (!cep || cep?.length < 8) && !focus && error?.errorCode === 'INCOMPLETE_CEP';
  const isFocusedUI = focus || !!cep || error?.errorCode === 'INCOMPLETE_CEP';

  return (
    <form onSubmit={handleSubmit}>
      <p>{label}</p>
      <Flexbox>
        <Cell>
          <CepBox $inputError={isInvalidCep || isIncompleteCep} $focus={focus} $isFocusedUI={isFocusedUI}>
            <span className="cep-placeholder" onClick={handleFocus}>
              Digite seu CEP
            </span>
            <input
              name="cep"
              ref={inputRef}
              maxLength={9}
              placeholder={isFocusedUI ? '00000-000' : ''}
              className="cep-input"
              onChange={handleChange}
              value={formatMask(cep, '00000-000')}
              onClick={handleFocus}
              onBlur={handleBlur}
            />
          </CepBox>
        </Cell>
        <Cell auto>
          <Button onClick={handleClick}>ok</Button>
        </Cell>
      </Flexbox>
      <FreightErrors error={error} />
    </form>
  );
};

const styleRadiusInput = css`
  border-radius: 8px;
`;

const transitionCep = css`
  transition: all 0.25s;
`;

const CepBox = styled.div`
  ${({ theme, $inputError, $focus, $isFocusedUI }) => css`
    ${styleRadiusInput}
    ${transitionCep}
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    height: 48px;
    padding: 0 16px;
    color: ${$focus ? theme.grey.darker : theme.grey.dark};
    border: 1px solid ${$inputError ? theme.red.dark : $focus ? theme.grey.darker : theme.grey.medium};
    background: ${$inputError ? theme.red.lighter : 'transparent'};

    &:hover {
      color: ${theme.grey.darker};
      border: 1px solid ${$inputError ? theme.red.dark : theme.grey.darker};

      .cep-placeholder {
        color: ${$inputError ? theme.red.dark : $isFocusedUI ? theme.grey.dark : theme.grey.darker};
      }
    }

    .cep-placeholder {
      width: 100%;
      color: ${$inputError ? theme.red.dark : theme.grey.dark};
      font-size: ${$isFocusedUI ? '12px' : '14px'};
      padding: ${$isFocusedUI ? '0' : '15px 0'};
      cursor: ${$isFocusedUI ? 'default' : 'text'};
      ${transitionCep}
    }

    .cep-input {
      ${styleRadiusInput}
      color: inherit;
      border: none;
      background: transparent;
      font-size: 14px;
      line-height: 22px;
      width: 100%;
      height: ${$isFocusedUI ? '22px' : 0};
      opacity: ${$isFocusedUI ? 1 : 0};
      ${transitionCep}

      ::placeholder,
      ::-webkit-input-placeholder {
        color: ${$isFocusedUI ? theme.grey.darker : theme.grey.dark};
        opacity: 1;
      }
      :-ms-input-placeholder {
        color: ${$isFocusedUI ? theme.grey.darker : theme.grey.dark};
        opacity: 1;
      }

      &:focus {
        outline: none;
      }
    }
  `}
`;

const Flexbox = styled.div`
  display: flex;
  margin-top: 5px;
`;

const Cell = styled.div`
  display: flex;
  flex: ${({ auto }) => (auto ? '1 1 0px' : 'auto')};
  width: 100%;
`;

const Button = styled.button`
  min-width: 50px;
  cursor: pointer;
  border: 2px solid ${({ theme }) => theme.buttonPrimaryColor};
  border-radius: 4px;
  background: #fff;
  color: ${({ theme }) => theme.red.light};
  margin-left: 10px;
  height: 100%;
  font-size: 16px;

  :focus {
    outline-color: ${({ theme }) => theme.red.light};
  }
`;

CepField.propTypes = {
  cookies: PropTypes.object,
  setCookie: PropTypes.func,
};

export default withCookies(CepField);
